import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useAuthData } from '../contexts/AuthData';
import { AuthPurpose, IdentityOperationError, IdentitySettings } from '../authIdentityApi';

export type ConnectedMethodLabels = {
  title: string; contactHint: string; connect: string; disconnect: string; confirm: string;
  cancel: string; verify: string; reauthenticate: string; email: string; sent: string;
  unavailable: string; retry: string; conflict: string; finalMethod: string; error: string;
};
export const DEFAULT_CONNECTED_LABELS: ConnectedMethodLabels = {
  title: 'Connected sign-in methods', contactHint: 'Contact email changes do not change your email sign-in method.',
  connect: 'Connect', disconnect: 'Disconnect', confirm: 'Confirm disconnect', cancel: 'Cancel',
  verify: 'Verify with', reauthenticate: 'Verify an existing sign-in method before continuing.',
  email: 'Email sign-in address', sent: 'Check your email and open the link in this browser.',
  unavailable: 'Connecting sign-in methods is not enabled yet.', retry: 'Retry',
  conflict: 'This method cannot be connected. Sign in to the intended account first.',
  finalMethod: 'Keep at least one sign-in method connected.', error: 'The operation could not be completed. Your account has not been switched.',
};
type Operation = { provider: string; email?: string; identityId?: number; awaitingReauthentication?: boolean; accountId?: string };

/**
 * ConnectedSignInMethods renders only the current account's safe identity metadata.
 * AuthContext owns all requests; translated labels have explicit English fallbacks.
 * Local failures preserve profile/cart state. Session storage retains an intended
 * sensitive operation across reauthentication redirects, never an account grant.
 * Provider evidence is completed only against the server-issued flow token.
 */
const ConnectedSignInMethods: React.FC<{ labels?: ConnectedMethodLabels }> = ({ labels = DEFAULT_CONNECTED_LABELS }) => {
  const auth = useAuthData();
  const actions = auth?.identityActions;
  const actionRef = useRef(actions); actionRef.current = actions;
  const [settings, setSettings] = useState<IdentitySettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [pending, setPending] = useState<Operation | null>(null);
  const [telegramToken, setTelegramToken] = useState<string | null>(null);
  const telegramRef = useRef<HTMLDivElement>(null);
  const key = 'ifoodPendingAuthOperation';

  const showError = (failure: unknown) => {
    if (failure instanceof IdentityOperationError) {
      if (failure.reason === 'reauthentication_required') { setError(labels.reauthenticate); return; }
      if (failure.reason === 'last_method') { setError(labels.finalMethod); return; }
      if (failure.reason === 'identity_conflict') { setError(labels.conflict); return; }
    }
    setError(labels.error);
  };
  const remember = (operation: Operation | null) => {
    const owned = operation ? { ...operation, accountId: auth?.authUser?.user_id ?? undefined } : null;
    setPending(owned);
    try {
      if (owned) sessionStorage.setItem(key, JSON.stringify(owned));
      else sessionStorage.removeItem(key);
    } catch { /* Storage-disabled browsers still support in-page verification. */ }
  };
  const reload = async () => {
    if (!actionRef.current) return;
    const next = await actionRef.current.list(); setSettings(next); return next;
  };
  const perform = async (operation: Operation, purpose: AuthPurpose = 'link') => {
    if (!actionRef.current) return;
    setBusy(true); setError(null); setStatus(null);
    try {
      if (operation.identityId !== undefined) {
        await actionRef.current.disconnect(operation.identityId);
        remember(null); setConfirmId(null); await reload();
      } else {
        const result = await actionRef.current.start(operation.provider, purpose, operation.email);
        if (result.redirect_url) { window.location.assign(result.redirect_url); return; }
        if (result.sent) setStatus(labels.sent);
        if (result.flow_token && operation.provider === 'telegram') setTelegramToken(result.flow_token);
      }
    } catch (failure) {
      if (failure instanceof IdentityOperationError && failure.reason === 'reauthentication_required') {
        remember({ ...operation, awaitingReauthentication: true });
        setSettings((previous) => previous ? { ...previous, reauthentication_required: true } : previous);
      }
      showError(failure);
    } finally { setBusy(false); }
  };
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const next = await reload();
        if (!active) return;
        let raw: string | null = null;
        try { raw = sessionStorage.getItem(key); } catch { /* Optional redirect continuation. */ }
        const operation = raw ? JSON.parse(raw) as Operation : null;
        if (operation && (!operation.accountId || operation.accountId !== auth?.authUser?.user_id)) {
          remember(null); return;
        }
        if (operation?.awaitingReauthentication && next && !next.reauthentication_required) {
          remember(null); await perform({ ...operation, awaitingReauthentication: false });
        } else if (operation) setPending(operation);
      } catch (failure) { if (active) showError(failure); }
    })();
    return () => { active = false; };
    // Actions are read from a ref because App recreates its context on data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!telegramToken || !telegramRef.current) return;
    const container = telegramRef.current;
    const callback = async (evidence: Record<string, unknown>) => {
      setBusy(true); setError(null);
      try {
        await actionRef.current?.completeTelegram(telegramToken, evidence);
        setTelegramToken(null);
        const operation = pending;
        remember(null); await reload();
        if (operation?.awaitingReauthentication) await perform({ ...operation, awaitingReauthentication: false });
      } catch (failure) { showError(failure); }
      finally { setBusy(false); }
    };
    window.handleIfoodConnectedTelegramAuth = (evidence) => { void callback(evidence); };
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22'; script.async = true;
    script.setAttribute('data-telegram-login', 'iFoodMeBot'); script.setAttribute('data-size', 'medium');
    script.setAttribute('data-onauth', 'handleIfoodConnectedTelegramAuth(user)'); container.appendChild(script);
    return () => { container.innerHTML = ''; delete window.handleIfoodConnectedTelegramAuth; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telegramToken, pending]);

  if (!actions) return null;
  const needsReauthentication = settings?.reauthentication_required || pending?.awaitingReauthentication;
  return <section aria-label={labels.title} className="bg-white rounded-4 border p-3">
    <h2 className="h6 fw-bold">{labels.title}</h2>
    <p className="small text-secondary">{labels.contactHint}</p>
    {busy && <Spinner size="sm" animation="border" role="status" />}
    {error && <Alert variant="warning">{error}</Alert>}
    {status && <div role="status">{status}</div>}
    {!settings && <Button type="button" size="sm" onClick={() => void reload().catch(showError)}>{labels.retry}</Button>}
    {settings?.identities.map((identity) => <div key={identity.id} className="d-flex justify-content-between gap-2 mb-2">
      <span>{identity.display}</span>
      <Button type="button" size="sm" variant="outline-danger" disabled={busy || !identity.can_disconnect || !settings.linking_enabled}
        onClick={() => setConfirmId(identity.id)}>{labels.disconnect}</Button>
    </div>)}
    {confirmId !== null && <div className="d-flex gap-2 mb-2">
      <Button type="button" size="sm" variant="danger" disabled={busy} onClick={() => void perform({ provider: '', identityId: confirmId })}>{labels.confirm}</Button>
      <Button type="button" size="sm" variant="light" onClick={() => setConfirmId(null)}>{labels.cancel}</Button>
    </div>}
    {settings && !settings.linking_enabled && <p className="small">{labels.unavailable}</p>}
    {settings?.linking_enabled && <>
      <Form.Group controlId="connected-method-email" className="mb-2">
        <Form.Label>{labels.email}</Form.Label>
        <Form.Control type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
      </Form.Group>
      {needsReauthentication && <p role="status">{labels.reauthenticate}</p>}
      <div className="d-flex flex-wrap gap-2">
        {(needsReauthentication ? [...new Set(settings.identities.map((row) => row.provider))] : settings.providers).map((provider) =>
          <Button key={provider} type="button" variant="outline-success" size="sm" disabled={busy || (provider === 'email' && !email.trim())}
            onClick={() => void perform({ provider, email }, needsReauthentication ? 'reauthenticate' : 'link')}>
            {needsReauthentication ? labels.verify : labels.connect} {provider}
          </Button>)}
      </div>
    </>}
    {telegramToken && <div className="mt-2">
      <div ref={telegramRef} />
      <Button type="button" size="sm" disabled={busy} onClick={() => {
        const initData = (window as unknown as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp?.initData;
        if (initData) void window.handleIfoodConnectedTelegramAuth?.({ init_data: initData });
        else setError(labels.error);
      }}>{labels.verify} Telegram Mini App</Button>
    </div>}
  </section>;
};

declare global { interface Window { handleIfoodConnectedTelegramAuth?: (evidence: Record<string, unknown>) => void; } }
export default ConnectedSignInMethods;
